/**
 * Size Systems Configuration
 *
 * IMPORTANT: This file now serves as SEED DATA and FALLBACK only.
 * 
 * PRIMARY SOURCE: Size systems are database-driven via the `sizeSystems` table.
 * 
 * Usage:
 * 1. SEED DATA: Used to populate the database via Platform Admin → Product Catalog → Size Systems
 * 2. FALLBACK: Used when database is not yet populated or query fails
 * 3. TYPE DEFINITIONS: Provides TypeScript types for size system IDs
 * 
 * To modify sizes:
 * - Go to Platform Admin → Product Catalog → Size Systems
 * - Edit sizes directly in the database
 * - Changes will reflect immediately in product forms
 * 
 * The product form now queries the database first, then falls back to these hardcoded values.
 */

export type SizeSystemType =
  | "international"
  | "uk"
  | "us"
  | "eu"
  | "asia"
  | "numeric"
  | "oneSize"
  | "shoes_us_women"
  | "shoes_us_men"
  | "shoes_eu"
  | "shoe_uk"
  | "bag_size"
  | "ring_size"
  | "belt_size"
  | "kids_age"
  | "hair_length"
  | "perfume_volume"
  | "cosmetic_volume"
  | "cosmetic_weight"
  | "pack_count";

export interface SizeSystem {
  id: SizeSystemType;
  name: string;
  description: string;
  sizes: string[];
  /** Visual category to group systems in tab pickers */
  category: "clothing" | "shoe" | "accessory" | "kids" | "hair" | "volume" | "weight" | "count" | "none";
}

export const SIZE_SYSTEMS: Record<SizeSystemType, SizeSystem> = {
  international: {
    id: "international",
    name: "International",
    description: "Standard international sizing",
    sizes: ["XXS", "XS", "S", "M", "L", "XL", "2XL", "3XL", "4XL", "5XL", "6XL"],
    category: "clothing",
  },
  uk: {
    id: "uk",
    name: "UK",
    description: "UK dress sizes",
    sizes: ["4", "6", "8", "10", "12", "14", "16", "18", "20", "22", "24", "26"],
    category: "clothing",
  },
  us: {
    id: "us",
    name: "US",
    description: "US dress sizes",
    sizes: ["00", "0", "2", "4", "6", "8", "10", "12", "14", "16", "18", "20", "22"],
    category: "clothing",
  },
  eu: {
    id: "eu",
    name: "EU",
    description: "European sizes",
    sizes: ["28", "30", "32", "34", "36", "38", "40", "42", "44", "46", "48", "50", "52", "54"],
    category: "clothing",
  },
  asia: {
    id: "asia",
    name: "Asian Sizes",
    description: "Asian sizing (height/bust)",
    sizes: ["155/80A", "160/84A", "165/88A", "170/92A", "175/96A", "180/100A"],
    category: "clothing",
  },
  numeric: {
    id: "numeric",
    name: "Numeric (Waist)",
    description: "Waist sizes for pants/jeans",
    sizes: ["24", "26", "28", "30", "32", "34", "36", "38", "40", "42", "44"],
    category: "clothing",
  },
  oneSize: {
    id: "oneSize",
    name: "One Size",
    description: "One size fits all",
    sizes: ["One Size"],
    category: "none",
  },
  shoes_us_women: {
    id: "shoes_us_women",
    name: "US Women",
    description: "US women's shoe sizes",
    sizes: ["4", "4.5", "5", "5.5", "6", "6.5", "7", "7.5", "8", "8.5", "9", "9.5", "10", "10.5", "11", "11.5", "12"],
    category: "shoe",
  },
  shoes_us_men: {
    id: "shoes_us_men",
    name: "US Men",
    description: "US men's shoe sizes",
    sizes: ["6", "6.5", "7", "7.5", "8", "8.5", "9", "9.5", "10", "10.5", "11", "11.5", "12", "12.5", "13", "14"],
    category: "shoe",
  },
  shoes_eu: {
    id: "shoes_eu",
    name: "EU",
    description: "European shoe sizes",
    sizes: ["35", "36", "37", "38", "39", "40", "41", "42", "43", "44", "45", "46", "47", "48"],
    category: "shoe",
  },
  shoe_uk: {
    id: "shoe_uk",
    name: "UK",
    description: "UK shoe sizes",
    sizes: ["3", "3.5", "4", "4.5", "5", "5.5", "6", "6.5", "7", "7.5", "8", "8.5", "9", "9.5", "10", "10.5", "11", "11.5", "12", "12.5", "13"],
    category: "shoe",
  },
  bag_size: {
    id: "bag_size",
    name: "Bag Size",
    description: "Bag sizes from mini to oversized",
    sizes: ["Mini", "Small", "Medium", "Large", "XL", "Oversized"],
    category: "accessory",
  },
  ring_size: {
    id: "ring_size",
    name: "Ring Size (UK)",
    description: "UK standard ring sizes",
    sizes: ["A", "B", "C", "D", "E", "F", "G", "H", "I", "J", "K", "L", "M", "N", "O", "P", "Q", "R", "S", "T", "U", "V", "W", "X", "Y", "Z", "Z+1", "Z+2", "Z+3"],
    category: "accessory",
  },
  belt_size: {
    id: "belt_size",
    name: "Belt Size",
    description: "Belt waist measurements",
    sizes: ["XS / 28\"", "S / 30\"", "M / 32\"", "L / 34\"", "XL / 36\"", "2XL / 38\"", "3XL / 40\""],
    category: "accessory",
  },
  kids_age: {
    id: "kids_age",
    name: "Kids by Age",
    description: "Kids sizing by age range",
    sizes: ["0–3m", "3–6m", "6–12m", "1Y", "2Y", "3Y", "4Y", "5Y", "6Y", "7Y", "8Y", "9Y", "10Y", "12Y", "14Y"],
    category: "kids",
  },
  hair_length: {
    id: "hair_length",
    name: "Hair Length",
    description: "Length of hair in inches for wigs, extensions, and bundles",
    sizes: ['8"', '10"', '12"', '14"', '16"', '18"', '20"', '22"', '24"', '26"', '28"', '30"', '32"', '34"', '36"', '38"', '40"'],
    category: "hair",
  },
  perfume_volume: {
    id: "perfume_volume",
    name: "Perfume Volume (ml)",
    description: "Common perfume / cologne / body-spray bottle sizes",
    sizes: ["5ml", "10ml", "15ml", "30ml", "50ml", "75ml", "100ml", "125ml", "150ml", "200ml"],
    category: "volume",
  },
  cosmetic_volume: {
    id: "cosmetic_volume",
    name: "Volume (ml / L)",
    description: "Liquid cosmetic & skincare bottle sizes — foundation, serum, moisturiser, shampoo, etc.",
    sizes: ["15ml", "30ml", "50ml", "75ml", "100ml", "150ml", "200ml", "250ml", "300ml", "400ml", "500ml", "750ml", "1L"],
    category: "volume",
  },
  cosmetic_weight: {
    id: "cosmetic_weight",
    name: "Weight (g)",
    description: "Solid / powder cosmetic weights — lipstick, eyeshadow, powder, soap, balm",
    sizes: ["3g", "5g", "10g", "15g", "20g", "25g", "30g", "50g", "75g", "100g", "150g", "200g", "250g", "500g"],
    category: "weight",
  },
  pack_count: {
    id: "pack_count",
    name: "Pack Count",
    description: "Number of units per pack — useful for multi-packs of cosmetics, toiletries, accessories",
    sizes: ["Single", "Pack of 2", "Pack of 3", "Pack of 5", "Pack of 6", "Pack of 10", "Pack of 12", "Pack of 24"],
    category: "count",
  },
};

/**
 * Get sizes for a given size system
 */
export function getSizesForSystem(system: SizeSystemType): string[] {
  return SIZE_SYSTEMS[system]?.sizes || SIZE_SYSTEMS.international.sizes;
}

/**
 * Get all size system options for dropdown
 */
export function getSizeSystemOptions(): { value: SizeSystemType; label: string }[] {
  return Object.values(SIZE_SYSTEMS).map((system) => ({
    value: system.id,
    label: system.name,
  }));
}

/**
 * Returns the visual category for a size system, used to switch tab sets in pickers.
 */
export function getSizeSystemCategory(
  system: SizeSystemType
): SizeSystem["category"] {
  return SIZE_SYSTEMS[system]?.category ?? "none";
}

/**
 * Default size systems for common product types by slug
 */
export const PRODUCT_TYPE_SIZE_DEFAULTS: Record<string, SizeSystemType> = {
  // Pants/Bottoms - numeric waist sizes
  jeans: "numeric",
  "jeans-men": "numeric",
  "jeans-women": "numeric",
  chinos: "numeric",
  "dress-pants": "numeric",
  "cargo-pants": "numeric",
  "cargo-pants-men": "numeric",
  "cargo-pants-women": "numeric",
  trousers: "numeric",
  "trousers-women": "numeric",

  // Dresses/Tops - international
  "maxi-dress": "international",
  "mini-dress": "international",
  "midi-dress": "international",
  blouse: "international",
  "t-shirt": "international",
  "t-shirt-men": "international",

  // UK sizing for formal wear
  "evening-dress": "uk",
  "cocktail-dress": "uk",
  "cocktail-dress-evening": "uk",
  "maxi-gown": "uk",
  "ball-gown": "uk",
  "a-line-princess": "uk",
  "mermaid-trumpet": "uk",
  "sheath-column": "uk",
  "empire-waist": "uk",
  "tea-length": "uk",
  "cape-gown": "uk",
  "slip-bias-cut": "uk",
  "overskirt-detachable": "uk",
  "backless-gown": "uk",
  "sequin-dress": "uk",
  "fit-flare": "uk",
  "cape-dress": "uk",

  // Accessories - one size or specific
  handbag: "bag_size",
  backpack: "bag_size",
  "tote-bag": "bag_size",
  "crossbody-bag": "bag_size",
  clutch: "bag_size",
  "messenger-bag": "bag_size",
  wallet: "oneSize",
  necklace: "oneSize",
  earrings: "oneSize",
  bracelet: "oneSize",
  watch: "oneSize",
  anklet: "oneSize",
  ring: "ring_size",
  belt: "belt_size",
  scarf: "oneSize",
  headwrap: "oneSize",
  "baseball-cap": "oneSize",
  beanie: "oneSize",
  "sun-hat": "oneSize",
  fedora: "oneSize",
  "bucket-hat": "oneSize",

  // Kids
  "girls-dress": "kids_age",
  "girls-top": "kids_age",
  "girls-skirt": "kids_age",
  "girls-pants": "kids_age",
  "boys-shirt": "kids_age",
  "boys-t-shirt": "kids_age",
  "boys-pants": "kids_age",
  "boys-shorts": "kids_age",

  // Footwear
  heels: "shoes_us_women",
  flats: "shoes_us_women",
  "sneakers-women": "shoes_us_women",
  "boots-women": "shoes_us_women",
  "sandals-women": "shoes_us_women",
  wedges: "shoes_us_women",
  pumps: "shoes_us_women",
  "loafers-women": "shoes_us_women",
  "oxford-shoes": "shoes_us_men",
  "loafers-men": "shoes_us_men",
  "sneakers-men": "shoes_us_men",
  "boots-men": "shoes_us_men",
  "sandals-men": "shoes_us_men",
  "dress-shoes": "shoes_us_men",

  // Beauty / Cosmetics — defaults now point at the most-likely real
  // sizing dimension for each product type so the form opens with the
  // right system pre-selected. Vendors can still switch to One Size
  // from the dropdown if they're selling a single-unit SKU.
  //
  // Liquids → ml volume (cosmetic_volume)
  foundation: "cosmetic_volume",
  moisturizer: "cosmetic_volume",
  cleanser: "cosmetic_volume",
  serum: "cosmetic_volume",
  sunscreen: "cosmetic_volume",
  toner: "cosmetic_volume",
  "face-wash": "cosmetic_volume",
  "body-lotion": "cosmetic_volume",
  "body-wash": "cosmetic_volume",
  shampoo: "cosmetic_volume",
  conditioner: "cosmetic_volume",
  "hair-oil": "cosmetic_volume",
  "hair-mask": "cosmetic_volume",
  "hair-serum": "cosmetic_volume",
  "nail-polish": "cosmetic_volume",
  // Solids / powders → grams (cosmetic_weight)
  lipstick: "cosmetic_weight",
  "lip-balm": "cosmetic_weight",
  "lip-gloss": "cosmetic_weight",
  eyeshadow: "cosmetic_weight",
  blush: "cosmetic_weight",
  bronzer: "cosmetic_weight",
  highlighter: "cosmetic_weight",
  "face-powder": "cosmetic_weight",
  "setting-powder": "cosmetic_weight",
  concealer: "cosmetic_weight",
  soap: "cosmetic_weight",
  // Single-unit items where size doesn't really vary
  mascara: "oneSize",
  eyeliner: "oneSize",
  "eyebrow-pencil": "oneSize",
  "makeup-brush": "oneSize",
  "beauty-blender": "oneSize",
  "hair-extensions": "oneSize",
  wigs: "oneSize",

  // Fragrance — bottled volume in millilitres. Even though the parent
  // category is beauty-cosmetics (which is hidden by default), having a
  // non-oneSize default here flips the size section back on (see the
  // override in isSizeSectionVisible / isSizeRequired below).
  perfume: "perfume_volume",
  cologne: "perfume_volume",
  fragrance: "perfume_volume",
  "eau-de-parfum": "perfume_volume",
  "eau-de-toilette": "perfume_volume",
  "body-spray": "perfume_volume",
  "body-mist": "perfume_volume",
  "after-shave": "perfume_volume",

  // Hair & Wigs — hair length
  "13x4-lace-frontal-wig": "hair_length",
  "13x6-lace-frontal-wig": "hair_length",
  "360-lace-wig": "hair_length",
  "hd-lace-wig": "hair_length",
  "transparent-lace-wig": "hair_length",
  "4x4-closure-wig": "hair_length",
  "5x5-closure-wig": "hair_length",
  "6x6-closure-wig": "hair_length",
  "2x6-closure-wig": "hair_length",
  "full-lace-wig": "hair_length",
  "glueless-full-lace-wig": "hair_length",
  "machine-made-wig": "hair_length",
  "headband-wig": "hair_length",
  "u-part-wig": "hair_length",
  "v-part-wig": "hair_length",
  "bob-wig": "hair_length",
  "pixie-wig": "hair_length",
  "long-wig": "hair_length",
  "layered-wig": "hair_length",
  "braided-wig": "hair_length",
  "ponytail-wig": "hair_length",
  "clip-in-extensions": "hair_length",
  "tape-in-extensions": "hair_length",
  "sew-in-weave": "hair_length",
  "ponytail-extension": "hair_length",
  "halo-extension": "hair_length",
  "micro-link-extension": "hair_length",
  "straight-bundles": "hair_length",
  "body-wave-bundles": "hair_length",
  "deep-wave-bundles": "hair_length",
  "curly-bundles": "hair_length",
  "loose-wave-bundles": "hair_length",
  "water-wave-bundles": "hair_length",
  // Closures/Frontals (standalone pieces) — one size
  "4x4-lace-closure": "oneSize",
  "5x5-lace-closure": "oneSize",
  "13x4-lace-frontal": "oneSize",
  "13x6-lace-frontal": "oneSize",
};

/**
 * Category slugs where size selection should be completely hidden.
 *
 * Previously this hid the entire `beauty-cosmetics` tree — but most
 * cosmetics DO have a real size dimension (ml for liquids, g for
 * solids), and our save flow requires a size, so hiding the picker
 * just made the form impossible to submit. Beauty now uses the
 * volume/weight/oneSize systems via `SLUG_TO_ALLOWED_SIZE_CATEGORIES`
 * below and the product-type defaults above.
 */
export const NO_SIZE_CATEGORY_SLUGS = new Set<string>([
  // intentionally empty — no fashion category is fully hidden anymore
]);

/**
 * Get default size system for a product type slug
 */
export function getDefaultSizeSystem(productTypeSlug: string): SizeSystemType {
  return PRODUCT_TYPE_SIZE_DEFAULTS[productTypeSlug] || "international";
}

// ==========================================
// Product-type → Allowed size system filtering
// ==========================================

/**
 * Maps product category/subcategory slugs to allowed SizeSystem categories.
 * Only size systems whose `.category` matches one of these will appear.
 */
const SLUG_TO_ALLOWED_SIZE_CATEGORIES: Record<string, SizeSystem["category"][]> = {
  // === CLOTHING (tops, dresses, outerwear, etc.) ===
  "womens-clothes": ["clothing"],
  "mens-clothing": ["clothing"],
  "dresses": ["clothing"],
  "tops": ["clothing"],
  "blouses": ["clothing"],
  "t-shirts": ["clothing"],
  "shirts": ["clothing"],
  "sweaters": ["clothing"],
  "jackets": ["clothing"],
  "coats": ["clothing"],
  "hoodies": ["clothing"],
  "activewear": ["clothing"],
  "swimwear": ["clothing"],
  "lingerie": ["clothing"],
  "suits": ["clothing"],
  "blazers": ["clothing"],
  "outerwear": ["clothing"],
  "evening-wear": ["clothing"],
  "formal-wear": ["clothing"],

  // === BOTTOMS (pants, jeans, skirts) ===
  "bottoms": ["clothing"],
  "pants": ["clothing"],
  "jeans": ["clothing"],
  "skirts": ["clothing"],
  "shorts": ["clothing"],
  "trousers": ["clothing"],
  "leggings": ["clothing"],

  // === FOOTWEAR (shoe systems only) ===
  "footwear": ["shoe"],
  "shoes": ["shoe"],
  "heels": ["shoe"],
  "sneakers": ["shoe"],
  "boots": ["shoe"],
  "sandals": ["shoe"],
  "flats": ["shoe"],
  "loafers": ["shoe"],

  // === BAGS (bag_size only) ===
  "bags": ["accessory"],
  "handbags": ["accessory"],
  "backpacks": ["accessory"],
  "crossbody": ["accessory"],

  // === JEWELRY (ring_size, oneSize) ===
  "jewelry": ["accessory", "none"],
  "rings": ["accessory"],
  "necklaces": ["none"],
  "earrings": ["none"],
  "bracelets": ["none"],

  // === ACCESSORIES (belts, scarves, hats — specific or oneSize) ===
  "accessories": ["accessory", "none"],
  "belts": ["accessory"],
  "scarves": ["none"],
  "hats": ["none"],
  "headwear": ["none"],

  // === KIDS (kids + clothing) ===
  "kids-wear": ["kids", "clothing"],
  "girls": ["kids", "clothing"],
  "boys": ["kids", "clothing"],
  "baby": ["kids"],

  // === BEAUTY & COSMETICS ===
  // Top-level beauty: allow all three real cosmetic dimensions plus
  // oneSize (always implicitly included). Subcategories below can
  // narrow this if a finer-grained allowlist is preferred.
  "beauty-cosmetics": ["volume", "weight", "count", "none"],
  "beauty": ["volume", "weight", "count", "none"],
  "cosmetics": ["volume", "weight", "count", "none"],

  // Fragrance — bottled volume (perfume_volume system)
  "fragrance": ["volume"],
  "fragrances": ["volume"],
  "perfume": ["volume"],
  "perfumes": ["volume"],
  "cologne": ["volume"],
  "body-sprays": ["volume"],

  // Skincare — typically ml
  "skincare": ["volume"],
  "skin-care": ["volume"],
  "face-care": ["volume"],
  "body-care": ["volume"],

  // Hair care liquids — ml
  "hair-care": ["volume"],
  "haircare": ["volume"],
  "hair-products": ["volume"],

  // Makeup — most products are sold by weight, some by volume
  "makeup": ["weight", "volume"],
  "make-up": ["weight", "volume"],
  "face-makeup": ["weight", "volume"],
  "eye-makeup": ["weight"],
  "lip-products": ["weight", "volume"],
  "nail-products": ["volume"],

  // Tools / brushes / single-unit items
  "beauty-tools": ["count", "none"],
  "makeup-tools": ["count", "none"],
  "hair-tools": ["count", "none"],

  // === HAIR & WIGS (hair length system) ===
  "hair-wigs": ["hair", "none"],
  "lace-front-wigs": ["hair"],
  "closure-wigs": ["hair"],
  "full-lace-wigs": ["hair"],
  "machine-made-wigs": ["hair"],
  "styled-wigs": ["hair"],
  "hair-extensions": ["hair"],
  "hair-bundles": ["hair"],
  "closures-frontals": ["none"],  // sold as one size
};

/**
 * Get filtered size system options based on product context.
 * For fashion: only shows relevant size systems (clothing for dresses, shoes for footwear, etc.)
 * For electronics: returns empty (size section hidden, uses specs instead)
 */
export function getFilteredSizeSystemOptions(
  industry: string,
  categorySlug: string,
  subcategorySlug?: string,
  productTypeSlug?: string
): { value: SizeSystemType; label: string }[] {
  // Electronics never use traditional sizes
  if (industry === "electronics") return [];

  // Check which size categories are allowed for this product
  // Priority: productType slug > subcategory slug > category slug
  const slugsToCheck = [productTypeSlug, subcategorySlug, categorySlug].filter(Boolean) as string[];

  let allowedCategories: SizeSystem["category"][] | null = null;

  for (const slug of slugsToCheck) {
    if (SLUG_TO_ALLOWED_SIZE_CATEGORIES[slug]) {
      allowedCategories = SLUG_TO_ALLOWED_SIZE_CATEGORIES[slug];
      break;
    }
  }

  // If no mapping found, check the product type defaults to determine category
  if (!allowedCategories && productTypeSlug) {
    const defaultSystem = PRODUCT_TYPE_SIZE_DEFAULTS[productTypeSlug];
    if (defaultSystem) {
      const systemCategory = SIZE_SYSTEMS[defaultSystem]?.category;
      if (systemCategory) {
        allowedCategories = [systemCategory];
        // Also include "none" (oneSize) as it's always relevant
        if (!allowedCategories.includes("none")) {
          allowedCategories.push("none");
        }
      }
    }
  }

  // If still no mapping, return all systems (safe fallback for fashion)
  if (!allowedCategories) {
    return getSizeSystemOptions();
  }

  // Empty array means hidden (beauty, etc.)
  if (allowedCategories.length === 0) return [];

  // Filter systems by allowed categories
  // Always include "oneSize" as it's universally applicable
  return Object.values(SIZE_SYSTEMS)
    .filter((system) => allowedCategories!.includes(system.category) || system.id === "oneSize")
    .map((system) => ({ value: system.id, label: system.name }));
}

// ==========================================
// Electronics Specification System
// ==========================================

export interface ElectronicsSpecConfig {
  label: string;
  /** Preset options to show as chips */
  presets: string[];
  /** Placeholder text for the custom input */
  customPlaceholder: string;
  /** Unit suffix for display (e.g. "GB", "mm") */
  unit?: string;
  /** Allow custom values to be added */
  allowCustom: boolean;
  /**
   * When true, this spec axis REPLACES the colour axis rather than augmenting
   * it — the product form hides the colour picker entirely. Used for products
   * that have no meaningful colour but vary by a single attribute instead, e.g.
   * screen protectors (Clear / Privacy / Matte) where "colour" makes no sense.
   */
  replacesColor?: boolean;
}

/**
 * Electronics specification options by category slug.
 * These replace "sizes" for electronics products.
 */
export const ELECTRONICS_SPEC_OPTIONS: Record<string, ElectronicsSpecConfig> = {
  // Smartphones — storage capacity
  "smartphones-mobile-phones": {
    label: "Storage Capacity",
    presets: ["16GB", "32GB", "64GB", "128GB", "256GB", "512GB", "1TB"],
    customPlaceholder: "e.g. 2TB",
    unit: "GB",
    allowCustom: true,
  },
  // Computers & Laptops — RAM/Storage config
  "computers-laptops": {
    label: "Configuration",
    presets: ["4GB/128GB", "8GB/256GB", "8GB/512GB", "16GB/256GB", "16GB/512GB", "16GB/1TB", "32GB/512GB", "32GB/1TB", "32GB/2TB", "64GB/1TB", "64GB/2TB"],
    customPlaceholder: "e.g. 128GB/4TB",
    allowCustom: true,
  },
  // Tablets & E-Readers — storage
  "tablets-e-readers": {
    label: "Storage Capacity",
    presets: ["16GB", "32GB", "64GB", "128GB", "256GB", "512GB", "1TB"],
    customPlaceholder: "e.g. 2TB",
    unit: "GB",
    allowCustom: true,
  },
  // TVs & Home Entertainment — screen size
  "tvs-home-entertainment": {
    label: "Screen Size",
    presets: ['24"', '27"', '32"', '40"', '43"', '50"', '55"', '65"', '75"', '85"', '98"'],
    customPlaceholder: 'e.g. 100"',
    unit: '"',
    allowCustom: true,
  },
  // Wearable Technology is intentionally NOT a category-level axis — it's a
  // grab-bag (smartwatches, rings, glasses, health monitors) where one axis
  // can't fit everything. The axis is set per-subcategory / per-product-type
  // instead (see ELECTRONICS_SUBCATEGORY/PRODUCT_TYPE_SPEC_OPTIONS below):
  // smartwatches → Case Size, smart-ring → Ring Size, everything else →
  // colour-only.
  // Gaming — no specs by default (mouse, controller, etc.)
  // Audio — no size for headphones/earbuds
  // Power — chargers/cables have no axis, but Power Banks get a Capacity axis
  // (mAh) at the subcategory level (see ELECTRONICS_SUBCATEGORY_SPEC_OPTIONS).
  // Cases — phone model (handled differently via attributes)

  // ── Automotive (lib/config/automotive.ts) ──
  // Tyres by size, as written on the sidewall — metric car sizes, then the
  // light-truck and truck sizes the shops in Kampala stock.
  "tyres-and-wheels": {
    label: "Tyre size",
    presets: [
      "155/80R13", "165/70R13", "175/70R13", "175/65R14", "185/65R14", "185/65R15", "195/65R15", "205/55R16",
      "205/65R16", "215/65R16", "225/65R17", "235/65R17", "265/65R17", "265/70R16", "7.00R16", "7.50R16",
      "8.25R16", "11R22.5", "12R22.5", "315/80R22.5",
    ],
    customPlaceholder: "e.g. 195/70R15",
    allowCustom: true,
  },
  "car-batteries": {
    label: "Capacity",
    presets: ["35Ah", "45Ah", "55Ah", "65Ah", "75Ah", "90Ah", "100Ah", "150Ah", "200Ah"],
    customPlaceholder: "e.g. 120Ah",
    unit: "Ah",
    allowCustom: true,
  },
  "oils-and-lubricants": {
    label: "Volume",
    presets: ["500ml", "1L", "4L", "5L", "20L", "208L"],
    customPlaceholder: "e.g. 2L",
    allowCustom: true,
  },
};

/**
 * Subcategory-level spec axes — override the (broad) category default. Used for
 * heterogeneous categories like Wearable Technology where one axis can't fit
 * every subcategory.
 */
export const ELECTRONICS_SUBCATEGORY_SPEC_OPTIONS: Record<string, ElectronicsSpecConfig> = {
  // Automotive: boda-boda and motorcycle tyres are sized differently, rims by diameter.
  "tyres-and-wheels-motorcycle-tyres": {
    label: "Tyre size",
    presets: ["2.50-17", "2.75-17", "2.75-18", "3.00-17", "3.00-18", "4.10-18", "90/90-18", "110/90-17"],
    customPlaceholder: "e.g. 3.25-18",
    allowCustom: true,
  },
  "tyres-and-wheels-wheels-and-rims": {
    label: "Rim size",
    presets: ['13"', '14"', '15"', '16"', '17"', '18"', '20"', '22.5"'],
    customPlaceholder: 'e.g. 19"',
    unit: '"',
    allowCustom: true,
  },
  // Smartwatches — OPTIONAL case size (only when the seller stocks the same
  // model in multiple sizes, e.g. Apple Watch 41/45mm). Left blank otherwise.
  "smartwatches": {
    label: "Case Size",
    presets: ["38mm", "40mm", "41mm", "42mm", "44mm", "45mm", "46mm", "49mm", "51mm"],
    customPlaceholder: "e.g. 52mm",
    unit: "mm",
    allowCustom: true,
  },
  // Power banks — sized by battery capacity (mAh). This is the spec buyers
  // actually compare, so it plays the variant-axis role (the same model often
  // ships in several capacities). Sellers can also add their own value (e.g. a
  // wattage or amperage rating) via the custom input.
  "power-banks": {
    label: "Capacity",
    presets: ["5000mAh", "10000mAh", "15000mAh", "20000mAh", "25000mAh", "26800mAh", "30000mAh", "40000mAh", "50000mAh"],
    customPlaceholder: "e.g. 60000mAh or 65W",
    unit: "mAh",
    allowCustom: true,
  },
  // Screen protectors — have NO colour; they vary by optical finish/type
  // (Clear, Privacy, Matte, ...). This axis REPLACES the colour picker so the
  // form isn't asking for a meaningless colour. Sellers can add their own.
  "screen-protectors": {
    label: "Finish",
    presets: ["Clear", "Privacy", "Matte / Anti-Glare", "Anti-Blue Light", "Anti-Fingerprint", "Mirror"],
    customPlaceholder: "e.g. Diamond",
    allowCustom: true,
    replacesColor: true,
  },
};

/**
 * Product-type-level spec axes — the most specific override (wins over
 * subcategory + category).
 */
export const ELECTRONICS_PRODUCT_TYPE_SPEC_OPTIONS: Record<string, ElectronicsSpecConfig> = {
  // Smart rings — sized by US ring size.
  "smart-ring": {
    label: "Ring Size (US)",
    presets: ["5", "6", "7", "8", "9", "10", "11", "12", "13"],
    customPlaceholder: "e.g. 14",
    allowCustom: true,
  },
  // Tablet screen protectors live under the Tablets category (whose default
  // axis is Storage), so override at product-type level: finish, not storage,
  // and no colour. Mirrors the "screen-protectors" subcategory config.
  "tablet-screen-protector": {
    label: "Finish",
    presets: ["Clear", "Privacy", "Matte / Anti-Glare", "Anti-Blue Light", "Anti-Fingerprint", "Mirror"],
    customPlaceholder: "e.g. Diamond",
    allowCustom: true,
    replacesColor: true,
  },
};

/**
 * Resolve the electronics spec axis for a product, most-specific first:
 * product type → subcategory → category. Returns null when none applies (the
 * product is then colour-only / simple — e.g. glasses, health monitors).
 */
export function getElectronicsSpecConfig(
  categorySlug: string,
  subcategorySlug?: string,
  productTypeSlug?: string,
): ElectronicsSpecConfig | null {
  if (productTypeSlug && ELECTRONICS_PRODUCT_TYPE_SPEC_OPTIONS[productTypeSlug]) {
    return ELECTRONICS_PRODUCT_TYPE_SPEC_OPTIONS[productTypeSlug];
  }
  if (subcategorySlug && ELECTRONICS_SUBCATEGORY_SPEC_OPTIONS[subcategorySlug]) {
    return ELECTRONICS_SUBCATEGORY_SPEC_OPTIONS[subcategorySlug];
  }
  return ELECTRONICS_SPEC_OPTIONS[categorySlug] || null;
}

// ==========================================
// Dynamic size requirement
// ==========================================

/**
 * Determine if sizes should be required for a product.
 * Fashion items with garment/shoe categories → required
 * Electronics → never required (uses specs or nothing)
 * Beauty → never required
 * OneSize default products → not required
 */
export function isSizeRequired(
  industry: string,
  categorySlug: string,
  productTypeSlug?: string
): boolean {
  // Electronics never require sizes
  if (industry === "electronics") return false;

  // Product-type override: if this product type has a real (non-oneSize)
  // size system mapping, it needs a size regardless of its parent
  // category. Lets perfume/cologne/etc. require a bottle volume even
  // though beauty-cosmetics is otherwise size-less.
  if (
    productTypeSlug &&
    PRODUCT_TYPE_SIZE_DEFAULTS[productTypeSlug] &&
    PRODUCT_TYPE_SIZE_DEFAULTS[productTypeSlug] !== "oneSize"
  ) {
    return true;
  }

  // Beauty doesn't require sizes
  if (NO_SIZE_CATEGORY_SLUGS.has(categorySlug)) return false;

  // If the product type defaults to oneSize, not required
  if (productTypeSlug && PRODUCT_TYPE_SIZE_DEFAULTS[productTypeSlug] === "oneSize") return false;

  // Fashion items generally require a size
  return true;
}

/**
 * Determine if the entire size/variant section should be hidden.
 * Hidden for beauty and electronics categories without specs, UNLESS a
 * specific product type opts back in via PRODUCT_TYPE_SIZE_DEFAULTS
 * (e.g. perfume → perfume_volume).
 */
export function isSizeSectionVisible(
  industry: string,
  categorySlug: string,
  productTypeSlug?: string,
  subcategorySlug?: string,
): boolean {
  if (industry === "electronics") {
    // Show if ANY level (product type → subcategory → category) defines a spec axis.
    return !!getElectronicsSpecConfig(categorySlug, subcategorySlug, productTypeSlug);
  }

  // Product-type override: even inside an otherwise-hidden category, a
  // product type with a real size system (perfume_volume, etc.) should
  // surface the section so the vendor can pick a bottle size.
  if (
    productTypeSlug &&
    PRODUCT_TYPE_SIZE_DEFAULTS[productTypeSlug] &&
    PRODUCT_TYPE_SIZE_DEFAULTS[productTypeSlug] !== "oneSize"
  ) {
    return true;
  }

  // Fashion: show unless it's beauty
  return !NO_SIZE_CATEGORY_SLUGS.has(categorySlug);
}
